/**
 * SimpleRecettesBoard — Onglet « Recettes » du cuisinier en mode simple (Juin 2026)
 *
 * Deux vues : À PRÉPARER (en attente + en cours) et TERMINÉS.
 * Une carte par plat : nom en gros, ingrédients à cocher d'un tap,
 * un seul gros bouton : COMMENCER → TERMINER.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChefHat, CheckCircle2, Timer, PlayCircle, Check, Utensils, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (s) => {
  if (!s || s < 0) return "—";
  const m = Math.floor(s / 60);
  if (m < 1) return `${s}s`;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, "0")}`;
  return `${Math.floor(h / 24)}j+`;
};

const useElapsed = (startedIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startedIso) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedIso]);
  if (!startedIso) return null;
  return Math.max(0, Math.floor((now - new Date(startedIso).getTime()) / 1000));
};

const RecipeCard = ({ order, currentUser, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const elapsed = useElapsed(order.status === "in_progress" ? order.started_at : null);
  const isPending = order.status === "pending";
  const isInProgress = order.status === "in_progress";
  const isDone = order.status === "completed";
  const ings = order.ingredients || [];
  const checked = ings.filter((i) => i.checked).length;
  const dishes = Array.isArray(order.dishes) && order.dishes.length > 0 ? order.dishes : [{ name: order.dish_name || "Plat", quantity: 1 }];
  const cookName = currentUser?.full_name || currentUser?.username || "Cuisinier";

  const toggle = async (idx, ing) => {
    if (isDone) return;
    try {
      await axios.patch(`${API}/kitchen-orders/${order.id}/toggle-ingredient`, {
        index: idx, checked: !ing.checked, actual_qty: parseFloat(ing.actual_qty ?? ing.expected_qty ?? 0),
      });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const start = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/kitchen-orders/${order.id}/start`, { cook_name: cookName, cook_id: currentUser?.id });
      toast.success("Préparation commencée");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const complete = async () => {
    let force = false;
    if (ings.length > 0 && checked === 0) {
      if (!window.confirm("Aucun ingrédient coché.\nTerminer quand même (le stock ne sera pas décrémenté) ?")) return;
      force = true;
    }
    if (ings.length === 0) force = true;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/kitchen-orders/${order.id}/complete`, { cook_name: cookName, photo_url: null, force });
      toast.success(`Plat terminé · ${r.data?.decremented_count || 0} produit(s) déstocké(s)`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const head = isDone ? "bg-emerald-500 text-slate-950" : isInProgress ? "bg-cyan-500 text-slate-950" : "bg-amber-500 text-slate-950";
  const border = isDone ? "border-emerald-500/40" : isInProgress ? "border-cyan-500/70" : "border-amber-500/60";

  return (
    <div className={`rounded-3xl border-2 bg-slate-900 overflow-hidden ${border}`} data-testid={`recipe-card-${order.id}`} data-status={order.status}>
      <div className={`px-5 py-3 ${head}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {dishes.map((d, i) => (
              <p key={i} className="text-2xl font-black leading-tight break-words">
                <span className="font-mono mr-2">×{Number(d.quantity || 1) * (order.batch_size > 1 ? order.batch_size : 1)}</span>{d.name}
              </p>
            ))}
            <p className="text-xs font-bold uppercase tracking-widest opacity-80 mt-1">
              {order.table_no ? `Table ${order.table_no}` : "Sans table"}{order.server_name ? ` · ${order.server_name}` : ""}
            </p>
          </div>
          <div className="text-right shrink-0">
            {isInProgress && elapsed !== null && (
              <div className="flex items-center gap-1 text-2xl font-black font-mono"><Timer className="w-5 h-5" /> {fmtTime(elapsed)}</div>
            )}
            {isDone && <div className="flex items-center gap-1 text-lg font-black font-mono"><CheckCircle2 className="w-5 h-5" /> {fmtTime(order.prep_time_seconds)}</div>}
            <p className="text-[10px] font-black uppercase tracking-widest opacity-90">
              {isDone ? "Terminé" : isInProgress ? "En cours" : "À préparer"}
            </p>
          </div>
        </div>
        {order.notes && <p className="text-sm italic mt-1 opacity-90">« {order.notes} »</p>}
      </div>

      {ings.length > 0 && (
        <div className="p-4 space-y-2">
          <p className="text-[11px] uppercase tracking-widest font-black text-slate-500">Ingrédients · {checked}/{ings.length}</p>
          {ings.map((ing, idx) => (
            <button
              key={idx}
              type="button"
              disabled={isDone}
              onClick={() => toggle(idx, ing)}
              className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 border-2 text-left transition-all active:scale-[0.98] ${
                ing.checked ? "bg-emerald-500/15 border-emerald-500/40 text-slate-400" : "bg-slate-800 border-slate-600 hover:border-emerald-400 text-white"
              } disabled:opacity-70`}
              data-testid={`recipe-ing-${order.id}-${idx}`}
            >
              <span className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0 ${ing.checked ? "bg-emerald-500 border-emerald-500 text-slate-950" : "border-slate-500"}`}>
                {ing.checked && <Check className="w-5 h-5" strokeWidth={3} />}
              </span>
              <span className={`flex-1 text-lg font-bold truncate ${ing.checked ? "line-through" : ""}`}>{ing.product_name}</span>
              <span className="font-mono text-base text-amber-300 shrink-0">{ing.expected_qty} {ing.unit}</span>
            </button>
          ))}
        </div>
      )}

      {!isDone && (
        <div className="px-4 pb-4">
          {isPending ? (
            <button type="button" onClick={start} disabled={busy}
              className="w-full rounded-2xl bg-cyan-500 hover:bg-cyan-400 active:scale-[0.98] text-slate-950 py-5 text-xl font-black flex items-center justify-center gap-2 transition-all disabled:opacity-60"
              data-testid={`recipe-start-${order.id}`}>
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <PlayCircle className="w-7 h-7" strokeWidth={2.5} />} COMMENCER
            </button>
          ) : (
            <button type="button" onClick={complete} disabled={busy}
              className="w-full rounded-2xl bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] text-slate-950 py-5 text-xl font-black flex items-center justify-center gap-2 transition-all disabled:opacity-60"
              data-testid={`recipe-complete-${order.id}`}>
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <CheckCircle2 className="w-7 h-7" strokeWidth={2.5} />} TERMINER
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const SimpleRecettesBoard = ({ currentUser }) => {
  const [view, setView] = useState("todo");
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (view === "todo") {
        const [p, ip] = await Promise.all([
          axios.get(`${API}/kitchen-orders`, { params: { status: "pending" } }),
          axios.get(`${API}/kitchen-orders`, { params: { status: "in_progress" } }),
        ]);
        const list = [...(ip.data?.orders || []), ...(p.data?.orders || [])];
        setOrders(list);
        setCounts(p.data?.counts || {});
      } else {
        const r = await axios.get(`${API}/kitchen-orders`, { params: { status: "completed" } });
        const today = new Date().toISOString().slice(0, 10);
        setOrders((r.data?.orders || []).filter((o) => (o.completed_at || o.updated_at || "").startsWith(today)));
        setCounts(r.data?.counts || {});
      }
    } catch { toast.error("Erreur de chargement des recettes"); }
    finally { setLoading(false); }
  }, [view]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (view !== "todo") return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [view, load]);

  const todoCount = (counts.pending || 0) + (counts.in_progress || 0);

  return (
    <div className="space-y-4" data-testid="simple-recettes-board">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setView("todo")}
          className={`flex-1 rounded-2xl py-4 text-lg font-black flex items-center justify-center gap-2 border-2 transition-all ${view === "todo" ? "bg-amber-500 border-amber-500 text-slate-950" : "bg-slate-900 border-slate-700 text-slate-300"}`}
          data-testid="recettes-view-todo">
          <Utensils className="w-5 h-5" /> À PRÉPARER
          {todoCount > 0 && <span className={`ml-1 rounded-full px-2.5 py-0.5 text-sm ${view === "todo" ? "bg-slate-950 text-amber-300" : "bg-amber-500 text-slate-950"}`}>{todoCount}</span>}
        </button>
        <button type="button" onClick={() => setView("done")}
          className={`flex-1 rounded-2xl py-4 text-lg font-black flex items-center justify-center gap-2 border-2 transition-all ${view === "done" ? "bg-emerald-500 border-emerald-500 text-slate-950" : "bg-slate-900 border-slate-700 text-slate-300"}`}
          data-testid="recettes-view-done">
          <CheckCircle2 className="w-5 h-5" /> TERMINÉS
        </button>
        <button type="button" onClick={load} disabled={loading} className="h-14 w-14 rounded-2xl bg-slate-900 border-2 border-slate-700 text-slate-300 flex items-center justify-center" data-testid="recettes-refresh">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
        </button>
      </div>

      {orders.length === 0 && !loading && (
        <div className="text-center py-16 text-slate-500" data-testid="recettes-empty">
          <ChefHat className="w-14 h-14 mx-auto mb-3 text-amber-400/50" />
          <p className="text-2xl font-black text-slate-300">{view === "todo" ? "Rien à préparer" : "Aucune recette terminée aujourd'hui"}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {orders.map((o) => <RecipeCard key={o.id} order={o} currentUser={currentUser} onChanged={load} />)}
      </div>
    </div>
  );
};

export default SimpleRecettesBoard;
